const $properties = Symbol.for('ViewController.properties');
const $onConnected = Symbol.for('ViewController.onConnected');
const $events = Symbol.for('ViewController.events');

/**
 * @typedef EwcElement
 * @property {Function} $dispatch
 * @extends HTMLElement
 */

/**
 * @extends EwcElement
 */
export class ViewController extends EwcElement {
  /**
   *
   * @param {Object} params
   * @param {string} [params.name] nombre del componente === nombre de la clase
   * @param {string} [params.template] template html
   * @param {string} [params.style] estilo css
   * @param {string} [params.label] titulo
   * @param {string} [params.icon] icono del titulo
   * @param {IconType} [params.iconType]
   * @param {boolean} [params.back] indica si mostrar el boton de regreso (flecha izquierda arriba)
   * @param {('view'|'form')} [params.footer] indica si mostrar el footer
   * @param {boolean} [params.isBtnAcceptDisabled] permite desabilitar el botón accept
   * @param {Object} [params.promise] template
   * @param {Function} [params.promise.getData] la function/promesa para recuperar los datos
   * @param {string} [params.promise.template] template que se va pintar cuando se resuelve la promesa con exito
   */
  constructor(params = {}) {
    super({
      shadow: true,
      componentName: params.name,
      shadowStyle: `:host{
        display: block;
        height: 100%;
        width: 100%;
        ::slotted(div[slot="aside"]) {
          height: 100%;
        }
      }
      ${params.style ?? ''}
      `,
      cacheTemplate: false,
      template: `<ewc-layout
        loading="{this.loading}"
        label="{this.label}"
        icon="{this.icon}"
        icon-type="{this.iconType}"
        back="{this.back}"
        footer="{this.footer}"
        is-btn-accept-disabled="{this.isBtnAcceptDisabled ? 'true' : 'false'}"
      >
        ${params.template ?? ''}
      </ewc-layout>`,
    });

    if (params.label) {
      this.label = params.label;
    }

    if (params.icon) {
      this.icon = params.icon;
    }

    if (params.iconType) {
      this.iconType = params.iconType;
    }

    if (params.back) {
      this.back = true;
    }

    if (params.footer) {
      this.footer = params.footer;
    }

    if (typeof params.isBtnAcceptDisabled === 'boolean') {
      this.isBtnAcceptDisabled = params.isBtnAcceptDisabled;
    }

    this.$dispatch('ViewController:opening');
  }

  static $properties = $properties;
  static $onConnected = $onConnected;
  static $onDestroy = EwcElement.$onDestroy;
  static $render = EwcElement.$render;
  static $templateContext = EwcElement.$templateContext;
  static $events = $events;
  static $watchers = EwcElement.$watchers;
  static $onResolved = EwcElement.$onResolved;
  static $onRejected = EwcElement.$onRejected;

  static get [EwcElement.$properties]() {
    return {
      loading: { type: 'Boolean', default: false },
      back: { type: 'Boolean' },
      label: { type: 'String' },
      icon: { type: 'String' },
      iconType: { type: 'String' },
      footer: { type: 'String' },
      isBtnAcceptDisabled: { type: 'Boolean', default: false },
      promise: {
        type: 'Promise',
        attribute: false,
      },

      ...(this[$properties] || {}),
    };
  }

  async [EwcElement.$onConnected]() {
    /** setTimeout para esperar que se ejecute el router `controller` si existe, para que inicialice los parametros */
    setTimeout(() => {
      this[$onConnected]?.();
    });
  }

  get [EwcElement.$events]() {
    return {
      'EwcLayout:back': () => {
        return this.$close();
      },
      'EwcLayout:close': () => {
        return this.$close();
      },
      'EwcLayout:cancel': async () => {
        /** Llamamos directamente el evento para poner el await (en vez de this.$dispatch('ViewController:cancel');) */
        await this[ViewController.$events]?.['ViewController:cancel']?.();
        requestAnimationFrame(() => {
          return this.$close();
        });
      },
      'EwcLayout:accept': async () => {
        /**
         * 'ViewController:accept' permite enganchar un evento antes del tratamiento normal del "accept"
         */

        /** Llamamos directamente el evento para poner el await (en vez de this.$dispatch('ViewController:accept');) */
        await this[ViewController.$events]?.['ViewController:accept']?.();
        requestAnimationFrame(() => {
          return this.$close();
        });
      },

      ...(this[$events] ?? {}),
    };
  }

  /**
   * Permite "cerrar" la vista. Realmente emite un evento estandar. El cierre lo hara el padre porque depende del contexto.
   * @param {object} [params]
   * @param {boolean} [params.forceRefresh] flag que indica si el padre debe ser recargado, o reload. Ej para un grid si se modifico un documento...
   */
  $close({ forceRefresh } = { forceRefresh: false }) {
    this.$dispatch({ name: 'ViewController:closing', detail: { forceRefresh } });
  }

  $state = {
    go: (stateName, params) => {
      return window.ediwin.router.go(stateName, params);
    },
    back: () => {
      return window.ediwin.router.back();
    },
  };
}

globalThis.ViewController = ViewController;
